import React, { useState } from 'react';
import { ChevronDown, AlertTriangle } from 'lucide-react';

/**
 * SelectionExplainer — how the panel chooses, collapsed by default.
 *
 * Nobody arrives at the Manage tab wanting the algorithm first; they arrive
 * wanting to fix the quote that just went up. So this stays shut, and the
 * per-display SelectionTrace lines carry the everyday answer. This is for the
 * second question — "why does it keep doing that" — which the traces raise and
 * cannot answer.
 *
 * Every number here comes from `selection_config` on the API response rather
 * than from a constant on this side. The band and the restart rate are tuned
 * against the live archive and have already moved once; a page describing the
 * previous tuning with total confidence is worse than no page.
 */

const pct = (n) => `${Math.round(n * 100)}%`;

/** The path a single draw takes, with the live constants filled in. */
const buildSteps = (config) => {
  const [lo, hi] = config.band || [0.65, 0.88];
  const cooldown = config.cooldown_hours || {};
  return [
    {
      k: 'Start from what is on the panel',
      v: 'The quote showing right now is the anchor. It is read off the display record rather '
        + 'than stored, so a second device would share the thread rather than walking its own.',
    },
    {
      k: 'Roll for a fresh start',
      v: `${pct(config.restart_rate)} of refreshes ignore the anchor entirely and draw from the whole `
        + 'archive. Without it a thread would run until it ran out of neighbours.',
    },
    {
      k: 'Drop anything shown in the last day',
      v: `Excluded outright, not just penalised. A thread circles one neighbourhood, and a soft `
        + `penalty lets the same quote back on screen within the hour.`,
    },
    {
      k: 'Rank the rest by closeness to the anchor',
      v: `The ${config.skip_top === 1 ? 'single closest match is' : `${config.skip_top} closest matches are`} `
        + 'thrown away first — the nearest neighbour is usually the same thing saved twice rather '
        + `than a rhyme — and the next ${config.candidates} are considered.`,
    },
    {
      k: 'Keep only what lands in the band',
      v: `Between ${lo} and ${hi}. Nothing in band means the thread has run out, and the draw `
        + 'restarts — which happens to roughly one quote in seven.',
    },
    {
      k: 'Among those, prefer what has rested',
      v: `Full weight past ${cooldown.semi_recent || 48} hours, half between ${cooldown.recent || 24} `
        + `and ${cooldown.semi_recent || 48}. So a thread still avoids the quotes it showed yesterday.`,
    },
  ];
};

const SelectionExplainer = ({ config }) => {
  const [open, setOpen] = useState(false);
  if (!config) return null;

  const [lo, hi] = config.band || [0.65, 0.88];
  const steps = buildSteps(config);

  return (
    <div className="mt-2 mb-4 text-center">
      <button
        onClick={() => setOpen(!open)}
        className="text-sm text-blue-500 hover:underline flex items-center justify-center mx-auto"
      >
        <span>How the panel chooses</span>
        <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-2 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 text-left text-sm animate-fadeIn">

          {/* The walk is the whole thing. If it is off or blind, say so before
              describing six steps that are not currently running. */}
          {(!config.walk_enabled || !config.vectors_available) && (
            <div className="flex items-start gap-2 mb-4 p-3 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <div className="text-xs">
                {!config.walk_enabled
                  ? 'The walk is switched off (INKQUOTE_SEMANTIC_WALK), so every refresh draws independently. The steps below describe what would happen with it on.'
                  : 'No vector file loaded, so the walk is dormant and draws fall back to plain weighted selection. Run scripts/embed_quotes.py.'}
              </div>
            </div>
          )}

          <div className="font-medium">The path a refresh takes</div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-3">
            Draws are not independent. Most of the time the next quote is a semantic neighbour of the
            one currently up, so a day of refreshes reads as a thread rather than as noise.
          </p>

          <ol className="space-y-2">
            {steps.map((step, i) => (
              <li key={step.k} className="flex gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <span className="min-w-0">
                  <span className="font-medium text-xs">{step.k}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{step.v}</span>
                </span>
              </li>
            ))}
          </ol>

          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="font-medium text-xs">How alike is alike enough</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              The walk wants a middle. Too far apart and there is no connection to notice; too close
              and it is the same quote twice — the same highlight saved from two places, or two bare
              links that match on being links.
            </p>
            <div className="flex mt-2 h-7 rounded overflow-hidden text-[10px] font-medium">
              <div className="flex-[65] bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 flex items-center justify-center">
                Unrelated
              </div>
              <div className="flex-[23] bg-blue-500 text-white flex items-center justify-center">
                What it wants
              </div>
              <div className="flex-[12] bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 flex items-center justify-center">
                Duplicate
              </div>
            </div>
            <div className="flex justify-between text-[10px] text-gray-400 dark:text-gray-500 mt-1">
              <span>0.00</span><span>{lo}</span><span>{hi}</span><span>1.00</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Those numbers are calibrated against this archive
              {config.model && <> under <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{config.model}</code></>}
              , not inherited — absolute cosine does not carry across embedding models. Unrelated
              pairs already reach 0.64, which is why the floor sits where it does.
            </p>
          </div>

          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="font-medium text-xs">Read the traces as inference, not as a log</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Nothing about a choice is written down when it happens — a display record is a
              timestamp and a duration. The lines above each quote are reconstructed afterwards from
              the record plus the vectors, which is what lets them cover history from before any of
              this existed. The cost: a fresh start that happens to land near the previous quote is
              indistinguishable afterwards from a hop, so a few &ldquo;followed on from&rdquo; lines
              are coincidence rather than intent.
            </p>
            {config.corpus_size > 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                {config.corpus_size.toLocaleString()} quotes carry a vector. Anything imported since
                the last run of embed_quotes.py has none, and reads as &ldquo;no reading on this one&rdquo;.
              </p>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default SelectionExplainer;
